import React, { useEffect, useMemo, useState } from "react";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";

const ClockTimePicker = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => {
  const [hour, setHour] = useState(parseInt(value.split(":")[0], 10));
  const [minute, setMinute] = useState(parseInt(value.split(":")[1], 10));

  const valFromRange = (v: number) => v.toString().padStart(2, "0");

  const hours = useMemo(
    () => Array.from({ length: 24 }, (_, index) => index),
    [],
  );
  const minutes = useMemo(
    () => Array.from({ length: 12 }, (_, index) => index * 5),
    [],
  );
  const quickMinutes = [0, 15, 30, 45];
  const clampHour = (nextHour: number) => ((nextHour % 24) + 24) % 24;

  useEffect(() => {
    const [nextHour, nextMinute] = value
      .split(":")
      .map((part) => parseInt(part, 10));
    setHour(Number.isNaN(nextHour) ? 0 : nextHour);
    setMinute(Number.isNaN(nextMinute) ? 0 : nextMinute);
  }, [value]);

  const adjustHour = (delta: number) => {
    setHour((currentHour) => clampHour(currentHour + delta));
  };

  const adjustMinute = (delta: number) => {
    setMinute((currentMinute) => {
      const currentIndex = minutes.indexOf(currentMinute);
      const safeIndex = currentIndex >= 0 ? currentIndex : 0;
      const nextIndex = (safeIndex + delta + minutes.length) % minutes.length;
      return minutes[nextIndex];
    });
  };

  const handleApply = () => {
    onChange(`${valFromRange(hour)}:${valFromRange(minute)}`);
  };

  const renderStepper = ({
    label,
    value: currentValue,
    onIncrement,
    onDecrement,
  }: {
    label: string;
    value: string;
    onIncrement: () => void;
    onDecrement: () => void;
  }) => (
    <div className="flex min-w-0 flex-col items-center">
      <span className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 sm:mb-4 sm:text-[11px] sm:tracking-[0.24em]">
        {label}
      </span>

      <button
        type="button"
        onClick={onIncrement}
        className="inline-flex h-7 w-7 items-center justify-center rounded-full text-primary transition hover:bg-[#f1f7f6] sm:h-8 sm:w-8"
        aria-label={`Increase ${label.toLowerCase()}`}
      >
        <ChevronUpIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
      </button>

      <div className="mt-1 flex min-w-[3.5rem] items-center justify-center px-1 text-[2.6rem] font-bold leading-none tracking-[-0.08em] text-slate-900 sm:min-w-[5rem] sm:px-2 sm:text-[3.8rem]">
        {currentValue}
      </div>

      <button
        type="button"
        onClick={onDecrement}
        className="mt-1 inline-flex h-7 w-7 items-center justify-center rounded-full text-primary transition hover:bg-[#f1f7f6] sm:h-8 sm:w-8"
        aria-label={`Decrease ${label.toLowerCase()}`}
      >
        <ChevronDownIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
      </button>
    </div>
  );

  return (
    <div className="w-fit max-w-full rounded-[28px] border border-[#e5e7eb] bg-white p-3 shadow-[0_20px_40px_rgba(15,23,42,0.12)] sm:max-w-[26rem] sm:rounded-[30px] sm:p-4">
      <div className="mb-4 flex items-center justify-between gap-3 sm:mb-5">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[1.05rem] font-bold tracking-[-0.03em] text-slate-900 sm:text-[1.3rem]">
            Select time
          </h3>
        </div>
        <span className="inline-flex shrink-0 items-center rounded-full border border-[#e5e7eb] bg-[#f3f4f6] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 sm:px-3 sm:py-1.5 sm:text-[11px] sm:tracking-[0.22em]">
          24 hour
        </span>
      </div>

      <div className="flex items-center justify-start gap-1 sm:gap-2.5">
        {renderStepper({
          label: "Hour",
          value: valFromRange(hour),
          onIncrement: () => adjustHour(1),
          onDecrement: () => adjustHour(-1),
        })}

        <div className="px-0 pt-7 text-[2.15rem] font-bold leading-none tracking-[-0.08em] text-slate-300 sm:px-0.5 sm:pt-8 sm:text-[3.1rem]">
          :
        </div>

        {renderStepper({
          label: "Minute",
          value: valFromRange(minute),
          onIncrement: () => adjustMinute(1),
          onDecrement: () => adjustMinute(-1),
        })}

        <div className="ml-2 flex min-w-[2.8rem] shrink-0 flex-col gap-1.5 pt-7 sm:ml-3 sm:min-w-[3.4rem] sm:gap-2 sm:pt-8">
          {quickMinutes.map((quickMinute) => (
            <button
              key={quickMinute}
              type="button"
              onClick={() => setMinute(quickMinute)}
              className={`inline-flex h-7 items-center justify-center rounded-full border px-1.5 text-[0.78rem] font-bold transition sm:h-9 sm:px-2 sm:text-[0.88rem] ${
                minute === quickMinute
                  ? "border-primary bg-primary text-white shadow-[0_10px_20px_rgba(0,128,128,0.18)]"
                  : "border-[#e5e7eb] bg-white text-slate-600 hover:bg-[#f3f4f6]"
              }`}
            >
              {valFromRange(quickMinute)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <button
          type="button"
          onClick={handleApply}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-[0_12px_24px_rgba(0,128,128,0.18)] transition hover:opacity-90 sm:px-5 sm:py-3"
        >
          <CheckIcon className="h-4 w-4" />
          Apply time
        </button>
      </div>
    </div>
  );
};

export default ClockTimePicker;
